import _get from 'lodash-es/get';
import { call, put } from 'redux-saga/effects';
import {
  getEmployeeDetailsFailed,
  getEmployeeDetailsSuccess,
  getAllEmployeesFailed,
  getAllEmployeesSuccess,
  getEmployeeStatusFailed,
  getEmployeeStatusSuccess,
  uploadEmployeesFailed,
  uploadEmployeesSuccess,
  setUploadMsg,
  getUploadStatusSuccess,
  getUploadStatusFailed,
  upsertEmployeeFailed,
  upsertEmployeeSuccess,
  getEmployeeDetailsInitiate,
  getAllEmployeesInitiate,
  getEmployeeSectionDataFailed,
  getEmployeeSectionDataSuccess,
  getEmployeesForGroupFailed,
  getEmployeesForGroupSuccess,
  getEmployeeCreditTransactionsFailed,
  getEmployeeCreditTransactionsSuccess,
  removeEmployeeFromCompanyFailed,
  removeEmployeeFromCompanySuccess,
  getEmployeeConsumptionsFailed,
  getEmployeeConsumptionsSuccess,
} from '../../actions/employee';
import {
  GET_ALL_EMPLOYEES,
  GET_EMPLOYEES_FOR_GROUP,
  GET_EMPLOYEE_CONSUMPTIONS,
  GET_EMPLOYEE_CREDIT_TRANSACTIONS,
  GET_EMPLOYEE_DETAILS,
  GET_EMPLOYEE_SECTION,
  REMOVE_EMPLOYEE_FROM_COMPANY,
  TOGGLE_EMPLOYEE_STATUS,
  UPLOAD_EMPLOYEES_URL,
  UPSERT_EMPLOYEE_URL,
} from '../../constants/urls';
import { clearInputValue } from '../../helpers';
import {
  employeeUploadMsg,
  genericErrorMsg,
} from '../../pages/common/components/constants';
import zfetch, {
  METHOD_GET,
  METHOD_POST,
} from '../../pages/common/utils/zfetch';
import { openErrorToast, openSuccessToast } from '../../reducers/uiLogic';

const upsertEmployee = (body = {}) => {
  const options = {
    body: JSON.stringify(body),
    method: METHOD_POST,
  };
  return zfetch(UPSERT_EMPLOYEE_URL, options);
};

const getUpsertEmployee = body => {
  const { name = '', email = '', contact = '', id = 0 } = body;

  const employee = {
    employee_name: name,
    email_id: email,
    contact_no: contact,
    employee_id: id,
  };

  return upsertEmployee(employee).then(res => res.json());
};

export function* upsertEmployeeSaga(action) {
  let employeeStatus = {};
  let successMessage = '';

  try {
    const jsonResponse = yield call(() => {
      return getUpsertEmployee(action.payload);
    });
    const { success = 1, message = {} } = jsonResponse;
    successMessage = _get(message, 'title', '');
    employeeStatus = success;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(openErrorToast());
    yield put(upsertEmployeeFailed(err));
    return;
  }

  yield put(openSuccessToast(successMessage));
  yield put(upsertEmployeeSuccess(employeeStatus));

  // Refresh employee details and employee table
  if (action.payload.id === 0) yield put(getAllEmployeesInitiate());
  else yield put(getEmployeeDetailsInitiate(action.payload.id));
}

const uploadEmployees = ({ fileBody }) => {
  const options = {
    body: fileBody,
    method: METHOD_POST,
  };
  return zfetch(UPLOAD_EMPLOYEES_URL, options);
};

export function* uploadEmployeesSaga(action) {
  let responseData = '';
  try {
    const response = yield uploadEmployees(action.payload);
    const jsonResponse = yield response.json();
    const {
      success = false,
      data: { process_id: processId = '', message = {} },
    } = jsonResponse;
    responseData = processId;
    if (success) {
      const successMessage = _get(message, 'title', employeeUploadMsg);
      yield put(setUploadMsg(successMessage));
      yield put(uploadEmployeesSuccess(responseData));
    } else {
      const failedMessage = _get(message, 'title', genericErrorMsg);
      yield put(openErrorToast(failedMessage));
      yield put(uploadEmployeesFailed());
    }
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    clearInputValue();
    yield put(openErrorToast());
    yield put(uploadEmployeesFailed(err));
  }
}

const getUploadStatus = processId => {
  const options = {
    method: METHOD_GET,
  };
  return zfetch(`${UPLOAD_EMPLOYEES_URL}/${processId}`, options);
};

export function* getUploadStatusSaga(action) {
  let responseData = {};

  try {
    const response = yield getUploadStatus(action.payload);
    const jsonResponse = yield response.json();
    const { success = false, data } = jsonResponse;
    responseData = data;
    const message = _get(data, 'message', {});
    if (success) {
      const successMessage = _get(message, 'title', employeeUploadMsg);
      yield put(setUploadMsg(successMessage));
      yield put(getUploadStatusSuccess(responseData));
    } else {
      const failedMessage = _get(message, 'title', genericErrorMsg);
      yield put(openErrorToast(failedMessage));
      yield put(getUploadStatusFailed());
    }
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(openErrorToast());
    yield put(getUploadStatusFailed(err));
  }
}

const getEmployeeSectionData = ({ workspaceId, flowType }) => {
  const options = {
    method: METHOD_GET,
    params: { workspace_id: workspaceId, flow_type: flowType },
  };
  return zfetch(GET_EMPLOYEE_SECTION, options);
};

export function* getEmployeeSectionSaga(action) {
  let employeeSection = {};

  try {
    const response = yield getEmployeeSectionData(action.payload);
    const jsonResponse = yield response.json();
    const { data } = jsonResponse;
    employeeSection = data;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(getEmployeeSectionDataFailed(err));
    return;
  }

  yield put(getEmployeeSectionDataSuccess(employeeSection));
}

const getAllEmployees = ({ workspaceId, flowType }) => {
  const options = {
    method: METHOD_GET,
    params: { workspace_id: workspaceId, flow_type: flowType },
  };
  return zfetch(GET_ALL_EMPLOYEES, options);
};

export function* getAllEmployeesSaga(action) {
  let employeesData = {};

  try {
    const response = yield getAllEmployees(action.payload);
    const jsonResponse = yield response.json();
    const { data } = jsonResponse;
    employeesData = data;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(getAllEmployeesFailed(err));
    return;
  }

  yield put(getAllEmployeesSuccess(employeesData));
}

const getEmployeesForGroup = params => {
  const { groupId = '', workspaceId = '' } = params;
  const options = {
    method: METHOD_GET,
    params: {
      group_id: groupId,
      workspace_id: workspaceId,
    },
  };

  return zfetch(GET_EMPLOYEES_FOR_GROUP, options);
};

export function* getEmployeesForGroupSaga(action) {
  let groupEmployees = {};
  try {
    const response = yield getEmployeesForGroup(action.payload);
    const jsonResponse = yield response.json();
    const { data } = jsonResponse;
    groupEmployees = data;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(getEmployeesForGroupFailed(err));
    return;
  }

  yield put(getEmployeesForGroupSuccess(groupEmployees));
}

const getEmployeeDetails = ({ emailHash, flowType }) => {
  const options = {
    method: METHOD_GET,
    params: { email_hash: emailHash, flow_type: flowType },
  };
  return zfetch(`${GET_EMPLOYEE_DETAILS}`, options);
};

export function* getEmployeeDetailsSaga(action) {
  let employeeData = {};
  try {
    const response = yield getEmployeeDetails(action.payload);
    const jsonResponse = yield response.json();
    const { data } = jsonResponse;
    employeeData = data;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(getEmployeeDetailsFailed(err));
    return;
  }

  yield put(getEmployeeDetailsSuccess(employeeData));
}

const getEmployeeCreditTransactions = emailHash => {
  const options = {
    method: METHOD_GET,
    params: { email_hash: emailHash },
  };
  return zfetch(GET_EMPLOYEE_CREDIT_TRANSACTIONS, options);
};

export function* getEmployeeCreditTransactionsSaga(action) {
  let creditTransactions = {};
  try {
    const response = yield getEmployeeCreditTransactions(action.payload);
    const jsonResponse = yield response.json();
    const { data } = jsonResponse;
    creditTransactions = data;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(getEmployeeCreditTransactionsFailed(err));
    return;
  }

  yield put(getEmployeeCreditTransactionsSuccess(creditTransactions));
}

const getEmployeeStatus = params => {
  const { id = '', activate = true } = params;

  const options = {
    method: METHOD_POST,
    body: JSON.stringify({
      activate,
    }),
  };
  return zfetch(`${TOGGLE_EMPLOYEE_STATUS}/${id}`, options);
};

export function* getEmployeeStatusSaga(action) {
  let employeeStatus = {};
  let successMessage = '';

  try {
    const response = yield getEmployeeStatus(action.payload);
    const jsonResponse = yield response.json();
    const {
      data: { status = 1, message = {} },
    } = jsonResponse;
    successMessage = _get(message, 'title', '');
    employeeStatus = status;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(openErrorToast());
    yield put(getEmployeeStatusFailed(err));
    return;
  }

  yield put(openSuccessToast(successMessage));
  yield put(getEmployeeStatusSuccess(employeeStatus));
}

const removeEmployeeFromCompany = (body = {}) => {
  const options = {
    method: METHOD_POST,
    body: JSON.stringify(body),
  };
  return zfetch(REMOVE_EMPLOYEE_FROM_COMPANY, options);
};

export function* removeEmployeeFromCompanySaga(action) {
  let message = '';
  try {
    const response = yield removeEmployeeFromCompany(action.payload);
    const jsonResponse = yield response.json();
    message = 'Employee removed from the company';
    const status = _get(jsonResponse, 'status', false);
    if (status) {
      yield put(openSuccessToast(message));
    } else {
      yield put(openErrorToast(message));
    }
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(openErrorToast());
    yield put(removeEmployeeFromCompanyFailed(err));
    return;
  }
  yield put(removeEmployeeFromCompanySuccess());
}

const getEmployeeConsumptions = ({
  businessType,
  emailHash,
  emailId,
  startDate,
  endDate,
}) => {
  let reqStartDate = startDate;
  let reqEndDate = endDate;

  if (!startDate || !endDate) {
    const today = new Date();
    const minus3Month = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    const firstDayOfMinus3Month = new Date(
      minus3Month.getFullYear(),
      minus3Month.getMonth(),
      2,
    );
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      1,
    );

    [reqStartDate] = firstDayOfMinus3Month.toISOString().split('T');
    [reqEndDate] = lastDayOfMonth.toISOString().split('T');
  }

  const options = {
    method: METHOD_GET,
    params: {
      business_type: businessType,
      email_hash: emailHash,
      email_id: emailId,
      start_date: reqStartDate,
      end_date: reqEndDate,
    },
  };

  return zfetch(GET_EMPLOYEE_CONSUMPTIONS, options);
};

export function* getEmployeeConsumptionsSaga(action) {
  let ordersData = {};

  try {
    const response = yield getEmployeeConsumptions(action.payload);
    const jsonResponse = yield response.json();
    const { data } = jsonResponse;

    ordersData = data;
  } catch (error) {
    let err = error;
    if (error.response) {
      err = yield err.response;
    }
    yield put(getEmployeeConsumptionsFailed(err));
    yield put(openErrorToast('Failed to get consumption orders for employee'));
    return;
  }

  yield put(getEmployeeConsumptionsSuccess(ordersData));
}
